import React from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import Header from './components/Header';
import ProjectCard from './components/ProjectCard';
import About from './components/About';
import Contact from './components/Contact';
import Footer from './components/Footer';
import { projects } from './data/projects';
import { GitCommitGenieCard } from './ApiCard';
import { ExplainPage } from './pages/ExplainPage';
import GitCommitGeniePage from './pages/GitCommitGeniePage';
import TextToStudyAidsPage from './pages/TextToStudyAidsPage';
import HRCareerToolsPage from './pages/HRCareerToolsPage';
import FinancialToolsPage from './pages/FinancialToolsPage';
import CreatorToolsPage from './pages/CreatorToolsPage';
import UrlShortenerPage from './pages/UrlShortenerPage';
import { isCardVisible } from './config/cardVisibility';

const AppContent = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex flex-col pt-16 transition-colors">
      <Header />
      <main className="flex-grow">
        <Routes>
          <Route path="/" element={
            <section id="projects" className="py-12 container mx-auto px-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Existing Projects - filtered by visibility config */}
                {projects
                  .filter(project => isCardVisible(project.title))
                  .map(project => (
                    <ProjectCard key={project.id} project={project} />
                  ))
                }
                
                {/* Explain Like I'm 5 */}
                {isCardVisible("Explain Like I'm 5") && (
                  <div>
                    <GitCommitGenieCard 
                      title="Explain Like I'm 5"
                      description="An AI-powered tool to simplify complex text into easy-to-understand explanations."
                      technologies={["React", "C# / .NET", "AI API"]}
                      onLaunch={() => navigate('/explain')}
                    />
                  </div>
                )}
                
                {/* Git Commit Genie */}
                {isCardVisible("Git Commit Genie") && (
                  <div>
                    <GitCommitGenieCard 
                      title="Git Commit Genie"
                      description="Paste a `git diff` and get AI-suggested conventional commit messages."
                      technologies={["React", "C# / .NET", "AI API"]}
                      onLaunch={() => navigate('/git-commit-genie')}
                    />
                  </div>
                )}
                
                {/* Jingle Generator moved to Content and Creator Tools section */}
                
                {/* Financial and Market Tools Card */}
                {isCardVisible("Financial and Market Tools") && (
                  <div>
                    <GitCommitGenieCard 
                      title="Financial and Market Tools"
                      description="Tools for analyzing financial news, market data, and investment insights."
                      technologies={["React", "C# / .NET", "AI API"]}
                      onLaunch={() => navigate('/financial-tools')}
                      buttonText="Launch Tools"
                    />
                  </div>
                )}
                
                {/* Content and Creator Tools Card */}
                {isCardVisible("Content and Creator Tools") && (
                  <div>
                    <GitCommitGenieCard 
                      title="Content and Creator Tools"
                      description="Tools for content creators, marketers, and social media professionals."
                      technologies={["React", "C# / .NET", "AI API"]}
                      onLaunch={() => navigate('/creator-tools')}
                      buttonText="Launch Tools"
                    />
                  </div>
                )}
                
                {/* Text-to-Study-Aids */}
                {isCardVisible("Text-to-Study-Aids") && (
                  <div>
                    <GitCommitGenieCard 
                      title="Text-to-Study-Aids"
                      description="Generate quizzes and flashcards automatically from any block of text."
                      technologies={["React", "C# / .NET", "AI API"]}
                      onLaunch={() => navigate('/text-to-study-aids')}
                      buttonText="Launch App"
                    />
                  </div>
                )}
                
                {/* HR/Career Tools Card */}
                {isCardVisible("HR/Career Tools") && (
                  <div>
                    <GitCommitGenieCard 
                      title="HR/Career Tools"
                      description="A suite of tools for HR professionals and job seekers to optimize the hiring process."
                      technologies={["React", "C# / .NET", "AI API"]}
                      onLaunch={() => navigate('/hr-career-tools')}
                      buttonText="Launch Tools"
                    />
                  </div>
                )}
                
                {/* AI Math Puzzle Generator */}
                {isCardVisible("AI Math Puzzle Generator") && (
                  <div>
                    <GitCommitGenieCard 
                      title="AI Math Puzzle Generator"
                      description="Generate unique math crossword puzzles for different grade levels."
                      technologies={["React", "C# / .NET", "AI API"]}
                      status="in-progress"
                    />
                  </div>
                )}
                
                {/* Daily Reflection Journal */}
                {isCardVisible("Daily Reflection Journal") && (
                  <div>
                    <GitCommitGenieCard 
                      title="Daily Reflection Journal"
                      description="A minimalist journal with daily AI-generated inspirational prompts."
                      technologies={["React", "C# / .NET", "Database"]}
                      status="in-progress"
                    />
                  </div>
                )}
                
                {/* Career tools moved to HR/Career Tools section */}
                
                {/* Smart Link Shortener moved to Content and Creator Tools section */}
                
                {/* Pantry Chef moved to Content and Creator Tools section */}
                
                {/* Financial tools moved to Financial and Market Tools section */}
                
                {/* Legal Document Summarizer */}
                {isCardVisible("Legal Document Summarizer") && (
                  <div>
                    <GitCommitGenieCard 
                      title="Legal Document Summarizer"
                      description="Explains confusing legal clauses in simple, easy-to-understand English."
                      technologies={["React", "C# / .NET", "AI API"]}
                      status="in-progress"
                    />
                  </div>
                )}
                
                {/* URL Shortener */}
                {isCardVisible("URL Shortener") && (
                  <div>
                    <GitCommitGenieCard 
                      title="URL Shortener"
                      description="Tool that transforms long URLs into powerful, AI-generated short links."
                      technologies={["React", "C# / .NET", "AI API", "Database"]}
                      onLaunch={() => navigate('/url-shortener')}
                      buttonText="Launch App"
                    />
                  </div>
                )}
              </div>
            </section>
          } />
          <Route path="/explain" element={<ExplainPage />} />
          <Route path="/git-commit-genie" element={<GitCommitGeniePage />} />
          <Route path="/text-to-study-aids" element={<TextToStudyAidsPage />} />
          <Route path="/hr-career-tools" element={<HRCareerToolsPage />} />
          <Route path="/financial-tools" element={<FinancialToolsPage />} />
          <Route path="/creator-tools" element={<CreatorToolsPage />} />
          <Route path="/url-shortener" element={<UrlShortenerPage />} />
        </Routes>
        <About />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return <AppContent />;
}

export default App;
